import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, UsersIcon, ClockIcon, Share2Icon, WrenchIcon, CheckCircle2Icon, AlertCircleIcon, CalendarIcon, TrophyIcon, ActivityIcon, SmartphoneIcon, ChevronDownIcon } from 'lucide-react';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ScrollReveal } from '../components/ui/ScrollReveal';
export function PlayAnalyticsPage() {
  const [dateRange, setDateRange] = useState('This Month');
  const keyMetrics = [{
    label: 'Total Rounds Played',
    value: '1,247',
    trend: '+18%',
    isPositive: true,
    icon: ActivityIcon
  }, {
    label: 'Unique Players',
    value: '892',
    trend: '+12%',
    isPositive: true,
    icon: UsersIcon
  }, {
    label: 'Avg. Round Time',
    value: '22m',
    trend: '-3m',
    isPositive: true,
    icon: ClockIcon
  }, {
    label: 'Social Shares',
    value: '486',
    trend: '+34%',
    isPositive: true,
    icon: Share2Icon
  }];
  const maintenanceTasks = [{
    task: 'Surface brushing',
    status: 'completed',
    date: 'Mar 12'
  }, {
    task: 'Hole cup replacement',
    status: 'completed',
    date: 'Mar 10'
  }, {
    task: 'Seam inspection',
    status: 'pending',
    date: 'Due Mar 19'
  }, {
    task: 'Deep clean',
    status: 'pending',
    date: 'Due Mar 25'
  }, {
    task: 'QR code check',
    status: 'pending',
    date: 'Due Mar 19'
  }];
  const recentActivity = [{
    action: 'Group of 4 finished round',
    detail: 'Winner: Sarah K. (Score: 21)',
    time: '10 mins ago',
    type: 'play'
  }, {
    action: 'Maintenance Logged',
    detail: 'Surface brushing completed by Team A',
    time: '2 hours ago',
    type: 'maintenance'
  }, {
    action: 'New Course Record!',
    detail: 'Alex M. scored 18 (Stroke Play)',
    time: '5 hours ago',
    type: 'record'
  }, {
    action: 'Result shared to Instagram',
    detail: 'By: Alex M.',
    time: '5 hours ago',
    type: 'social'
  }, {
    action: 'Group of 2 started round',
    detail: 'Mode: Match Play',
    time: '6 hours ago',
    type: 'play'
  }, {
    action: 'Equipment Alert',
    detail: 'Putter inventory low (2 need grip replacement)',
    time: '1 day ago',
    type: 'alert'
  }];
  return <div className="w-full bg-brand-cream-light min-h-screen pb-24" data-id="element-768">
      {/* Hero Header */}
      <section className="pt-32 pb-20 bg-brand-green-deep text-brand-cream-light relative overflow-hidden" data-id="element-769">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(184,150,90,0.15)_0%,transparent_60%)]" data-id="element-770" />

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-771">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8" data-id="element-772">
            <div data-id="element-773">
              <span className="text-brand-gold text-sm font-bold uppercase tracking-widest mb-4 block" data-id="element-774">
                Venue Dashboard
              </span>
              <h1 className="text-4xl md:text-5xl font-heading mb-4" data-id="element-775">
                PuttLab Play Analytics
              </h1>
              <p className="text-brand-cream/70 font-body text-lg max-w-xl" data-id="element-776">
                Track engagement, monitor performance, and manage your venue
                maintenance in real-time.
              </p>
            </div>

            <div className="flex flex-col gap-4" data-id="element-777">
              {/* Venue Selector Mock */}
              <button className="flex items-center justify-between bg-brand-cream/10 border border-brand-cream/20 rounded-lg px-4 py-3 min-w-[280px] hover:bg-brand-cream/20 transition-colors" data-id="element-778">
                <div className="text-left" data-id="element-779">
                  <span className="block text-[10px] text-brand-cream/50 uppercase tracking-wider" data-id="element-780">
                    Current Venue
                  </span>
                  <span className="block font-body text-sm font-medium" data-id="element-781">
                    The Sunset Green — Fern Resort, Orillia
                  </span>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-brand-gold" data-id="element-782" />
              </button>

              {/* Date Range Tabs */}
              <div className="flex bg-brand-charcoal/50 p-1 rounded-lg border border-brand-cream/10" data-id="element-783">
                {['Today', 'This Week', 'This Month', 'All Time'].map(range => <button key={range} onClick={() => setDateRange(range)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${dateRange === range ? 'bg-brand-gold text-brand-charcoal shadow-sm' : 'text-brand-cream/50 hover:text-brand-cream-light'}`} data-id="element-784">
                      {range}
                    </button>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 -mt-8 relative z-20" data-id="element-785">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-786">
          {keyMetrics.map((metric, i) => <ScrollReveal key={metric.label} delay={i * 0.1} data-id="element-787">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-brand-stone/10" data-id="element-788">
                <div className="flex items-center justify-between mb-4" data-id="element-789">
                  <div className="p-3 bg-brand-cream rounded-xl" data-id="element-790">
                    <metric.icon className="w-5 h-5 text-brand-green" data-id="element-791" />
                  </div>
                  <span className={`text-sm font-bold flex items-center ${metric.isPositive ? 'text-green-600' : 'text-red-500'}`} data-id="element-792">
                    {metric.trend}
                  </span>
                </div>
                <h3 className="text-3xl font-heading text-brand-green-deep mb-1" data-id="element-793">
                  {metric.value}
                </h3>
                <p className="text-xs font-body text-brand-stone uppercase tracking-wider" data-id="element-794">
                  {metric.label}
                </p>
              </div>
            </ScrollReveal>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12" data-id="element-795">
          {/* Game Mode Breakdown */}
          <ScrollReveal className="lg:col-span-1" data-id="element-796">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10 h-full" data-id="element-797">
              <h3 className="text-xl font-heading text-brand-green-deep mb-6" data-id="element-798">
                Game Modes
              </h3>

              {/* Mock Donut Chart */}
              <div className="relative w-48 h-48 mx-auto mb-8" data-id="element-799">
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90" data-id="element-800">
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#F5F0E8" strokeWidth="20" data-id="element-801" />
                  {/* Stroke Play 45% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#1B3A2D" strokeWidth="20" strokeDasharray="113 251" strokeDashoffset="0" data-id="element-802" />
                  {/* Match Play 25% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#B8965A" strokeWidth="20" strokeDasharray="63 251" strokeDashoffset="-113" data-id="element-803" />
                  {/* Challenge 20% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#C4A97D" strokeWidth="20" strokeDasharray="50 251" strokeDashoffset="-176" data-id="element-804" />
                  {/* Best Ball 10% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#8A8578" strokeWidth="20" strokeDasharray="25 251" strokeDashoffset="-226" data-id="element-805" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-806">
                  <span className="text-3xl font-heading text-brand-green-deep" data-id="element-807">
                    1.2k
                  </span>
                  <span className="text-[10px] text-brand-stone uppercase tracking-widest" data-id="element-808">
                    Rounds
                  </span>
                </div>
              </div>

              <div className="space-y-4" data-id="element-809">
                {[{
                name: 'Stroke Play',
                pct: '45%',
                count: '562',
                color: 'bg-brand-green'
              }, {
                name: 'Match Play',
                pct: '25%',
                count: '312',
                color: 'bg-brand-gold'
              }, {
                name: 'Resort Challenge',
                pct: '20%',
                count: '249',
                color: 'bg-brand-sand'
              }, {
                name: 'Best Ball',
                pct: '10%',
                count: '124',
                color: 'bg-brand-stone'
              }].map(mode => <div key={mode.name} className="flex items-center justify-between text-sm" data-id="element-810">
                    <div className="flex items-center space-x-2" data-id="element-811">
                      <div className={`w-3 h-3 rounded-full ${mode.color}`} data-id="element-812" />
                      <span className="font-medium text-brand-charcoal" data-id="element-813">
                        {mode.name}
                      </span>
                    </div>
                    <div className="text-right" data-id="element-814">
                      <span className="font-bold text-brand-green-deep mr-2" data-id="element-815">
                        {mode.pct}
                      </span>
                      <span className="text-brand-stone text-xs" data-id="element-816">
                        ({mode.count})
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </ScrollReveal>

          {/* Weekly Activity Chart */}
          <ScrollReveal className="lg:col-span-2" delay={0.2} data-id="element-817">
            <div className="bg-brand-green-deep rounded-2xl p-8 shadow-lg h-full flex flex-col" data-id="element-818">
              <div className="flex justify-between items-center mb-8" data-id="element-819">
                <div data-id="element-820">
                  <h3 className="text-xl font-heading text-brand-cream-light mb-1" data-id="element-821">
                    Weekly Activity
                  </h3>
                  <p className="text-sm text-brand-cream/60 font-body" data-id="element-822">
                    Rounds played over the last 7 days
                  </p>
                </div>
                <div className="p-3 bg-brand-cream/10 rounded-xl" data-id="element-823">
                  <BarChart3Icon className="w-5 h-5 text-brand-gold" data-id="element-824" />
                </div>
              </div>

              {/* Bar Chart Mockup */}
              <div className="flex-1 flex items-end justify-between gap-2 md:gap-4 pt-8" data-id="element-825">
                {[{
                day: 'Mon',
                val: 32,
                height: '40%'
              }, {
                day: 'Tue',
                val: 28,
                height: '35%'
              }, {
                day: 'Wed',
                val: 45,
                height: '55%'
              }, {
                day: 'Thu',
                val: 38,
                height: '45%'
              }, {
                day: 'Fri',
                val: 52,
                height: '65%'
              }, {
                day: 'Sat',
                val: 78,
                height: '100%'
              }, {
                day: 'Sun',
                val: 65,
                height: '85%'
              }].map((d, i) => <div key={d.day} className="flex flex-col items-center flex-1 group" data-id="element-826">
                    <div className="w-full relative flex justify-center h-48 md:h-64 items-end" data-id="element-827">
                      <motion.div initial={{
                    height: 0
                  }} whileInView={{
                    height: d.height
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: i * 0.1,
                    type: 'spring'
                  }} className="w-full max-w-[40px] bg-brand-gold rounded-t-md relative group-hover:bg-brand-gold-light transition-colors" data-id="element-828">
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-brand-charcoal text-brand-cream-light text-xs py-1 px-2 rounded font-bold" data-id="element-829">
                          {d.val}
                        </div>
                      </motion.div>
                    </div>
                    <span className="text-brand-cream/60 text-xs font-bold uppercase tracking-widest mt-4" data-id="element-830">
                      {d.day}
                    </span>
                  </div>)}
              </div>
            </div>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-id="element-831">
          {/* Maintenance & Venue Health */}
          <ScrollReveal data-id="element-832">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10 h-full" data-id="element-833">
              <div className="flex justify-between items-start mb-8" data-id="element-834">
                <div data-id="element-835">
                  <h3 className="text-xl font-heading text-brand-green-deep mb-1" data-id="element-836">
                    Maintenance & Health
                  </h3>
                  <p className="text-sm text-brand-stone font-body" data-id="element-837">
                    Keep your venue in premium condition
                  </p>
                </div>
                <div className="flex items-center space-x-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full border border-green-200" data-id="element-838">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-839" />
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-840">
                    Excellent
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-841">
                <div className="bg-brand-cream p-4 rounded-xl" data-id="element-842">
                  <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-843">
                    Last Maintenance
                  </span>
                  <span className="font-bold text-brand-green-deep" data-id="element-844">
                    March 12, 2026
                  </span>
                </div>
                <div className="bg-brand-cream p-4 rounded-xl" data-id="element-845">
                  <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-846">
                    Next Scheduled
                  </span>
                  <span className="font-bold text-brand-gold" data-id="element-847">
                    March 19, 2026
                  </span>
                </div>
              </div>

              <h4 className="text-sm font-bold text-brand-charcoal uppercase tracking-wider mb-4" data-id="element-848">
                Task Checklist
              </h4>
              <div className="space-y-3 mb-8" data-id="element-849">
                {maintenanceTasks.map((task, i) => <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-brand-stone/10 bg-gray-50/50" data-id="element-850">
                    <div className="flex items-center space-x-3" data-id="element-851">
                      {task.status === 'completed' ? <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-852" /> : <div className="w-5 h-5 rounded-full border-2 border-brand-gold/50 flex items-center justify-center" data-id="element-853">
                          <div className="w-2 h-2 bg-brand-gold rounded-full" data-id="element-854" />
                        </div>}
                      <span className={`text-sm font-medium ${task.status === 'completed' ? 'text-brand-stone line-through' : 'text-brand-charcoal'}`} data-id="element-855">
                        {task.task}
                      </span>
                    </div>
                    <span className="text-xs text-brand-stone" data-id="element-856">
                      {task.date}
                    </span>
                  </div>)}
              </div>

              <h4 className="text-sm font-bold text-brand-charcoal uppercase tracking-wider mb-4" data-id="element-857">
                Equipment Status
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-858">
                <div className="p-3 rounded-lg border border-red-200 bg-red-50" data-id="element-859">
                  <span className="text-xs font-bold text-red-700 block mb-1" data-id="element-860">
                    Putters
                  </span>
                  <span className="text-xs text-red-600" data-id="element-861">
                    12 available, 2 need grip replacement
                  </span>
                </div>
                <div className="p-3 rounded-lg border border-green-200 bg-green-50" data-id="element-862">
                  <span className="text-xs font-bold text-green-700 block mb-1" data-id="element-863">
                    Scorecards/QR
                  </span>
                  <span className="text-xs text-green-600" data-id="element-864">All functional</span>
                </div>
                <div className="p-3 rounded-lg border border-green-200 bg-green-50" data-id="element-865">
                  <span className="text-xs font-bold text-green-700 block mb-1" data-id="element-866">
                    Lighting
                  </span>
                  <span className="text-xs text-green-600" data-id="element-867">
                    Operational (check Apr 1)
                  </span>
                </div>
                <div className="p-3 rounded-lg border border-green-200 bg-green-50" data-id="element-868">
                  <span className="text-xs font-bold text-green-700 block mb-1" data-id="element-869">
                    Hole Cups
                  </span>
                  <span className="text-xs text-green-600" data-id="element-870">
                    Replaced Mar 10
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Player Engagement & Social */}
          <div className="space-y-8" data-id="element-871">
            <ScrollReveal delay={0.2} data-id="element-872">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10" data-id="element-873">
                <h3 className="text-xl font-heading text-brand-green-deep mb-6" data-id="element-874">
                  Player Engagement
                </h3>

                <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-875">
                  <div data-id="element-876">
                    <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-877">
                      Avg Group Size
                    </span>
                    <span className="text-3xl font-heading text-brand-gold" data-id="element-878">
                      2.8{' '}
                      <span className="text-sm font-body text-brand-stone" data-id="element-879">
                        players
                      </span>
                    </span>
                  </div>
                  <div data-id="element-880">
                    <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-881">
                      Return Rate (7d)
                    </span>
                    <span className="text-3xl font-heading text-brand-gold" data-id="element-882">
                      34%
                    </span>
                  </div>
                </div>

                <div className="mb-2" data-id="element-883">
                  <span className="text-xs font-bold text-brand-charcoal uppercase tracking-wider block mb-3" data-id="element-884">
                    Peak Hours Heatmap
                  </span>
                  <div className="flex h-8 rounded-lg overflow-hidden" data-id="element-885">
                    <div className="flex-1 bg-brand-stone/20" title="8am-10am (Low)" data-id="element-886" />
                    <div className="flex-[2] bg-brand-sand/60" title="10am-2pm (Moderate)" data-id="element-887" />
                    <div className="flex-[3] bg-brand-gold" title="2pm-5pm (Peak)" data-id="element-888" />
                    <div className="flex-[2] bg-brand-sand/60" title="5pm-7pm (Moderate)" data-id="element-889" />
                    <div className="flex-[3] bg-brand-stone/20" title="7pm-10pm (Low)" data-id="element-890" />
                  </div>
                  <div className="flex justify-between text-[10px] text-brand-stone mt-1" data-id="element-891">
                    <span data-id="element-892">8am</span>
                    <span data-id="element-893">12pm</span>
                    <span data-id="element-894">4pm</span>
                    <span data-id="element-895">8pm</span>
                    <span data-id="element-896">10pm</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-897">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10" data-id="element-898">
                <div className="flex justify-between items-center mb-6" data-id="element-899">
                  <h3 className="text-xl font-heading text-brand-green-deep" data-id="element-900">
                    Social & Content
                  </h3>
                  <div className="p-2 bg-pink-50 rounded-lg" data-id="element-901">
                    <InstagramIcon className="w-5 h-5 text-pink-600" data-id="element-902" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-903">
                  <div className="flex justify-between items-center p-3 bg-brand-cream rounded-xl" data-id="element-904">
                    <span className="text-sm font-medium text-brand-charcoal" data-id="element-905">
                      Share Conversion Rate
                    </span>
                    <span className="font-bold text-brand-green-deep" data-id="element-906">39%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-brand-cream rounded-xl" data-id="element-907">
                    <span className="text-sm font-medium text-brand-charcoal" data-id="element-908">
                      Top Sharing Day
                    </span>
                    <span className="font-bold text-brand-green-deep" data-id="element-909">
                      Saturday (avg 18)
                    </span>
                  </div>
                  <div className="p-4 border border-brand-stone/20 rounded-xl" data-id="element-910">
                    <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-2" data-id="element-911">
                      Most Shared Result
                    </span>
                    <div className="flex items-center space-x-3" data-id="element-912">
                      <div className="w-10 h-10 bg-brand-gold/20 rounded-full flex items-center justify-center" data-id="element-913">
                        <TrophyIcon className="w-5 h-5 text-brand-gold" data-id="element-914" />
                      </div>
                      <div data-id="element-915">
                        <p className="text-sm font-bold text-brand-charcoal" data-id="element-916">
                          Alex M. Course Record
                        </p>
                        <p className="text-xs text-brand-stone" data-id="element-917">
                          Shared 12 times this week
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <ScrollReveal data-id="element-918">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10" data-id="element-919">
            <h3 className="text-xl font-heading text-brand-green-deep mb-6" data-id="element-920">
              Recent Activity Log
            </h3>

            <div className="space-y-0" data-id="element-921">
              {recentActivity.map((activity, i) => {
              let Icon = ActivityIcon;
              let colorClass = 'text-brand-green bg-brand-green/10';
              if (activity.type === 'maintenance') {
                Icon = WrenchIcon;
                colorClass = 'text-blue-600 bg-blue-50';
              }
              if (activity.type === 'record') {
                Icon = TrophyIcon;
                colorClass = 'text-brand-gold bg-brand-gold/10';
              }
              if (activity.type === 'social') {
                Icon = Share2Icon;
                colorClass = 'text-pink-600 bg-pink-50';
              }
              if (activity.type === 'alert') {
                Icon = AlertCircleIcon;
                colorClass = 'text-red-600 bg-red-50';
              }
              return <div key={i} className="flex items-start space-x-4 p-4 border-b border-brand-stone/10 last:border-0 hover:bg-gray-50 transition-colors rounded-lg" data-id="element-922">
                    <div className={`p-2 rounded-lg ${colorClass} shrink-0 mt-1`} data-id="element-923">
                      <Icon className="w-4 h-4" data-id="element-924" />
                    </div>
                    <div className="flex-1" data-id="element-925">
                      <div className="flex justify-between items-start mb-1" data-id="element-926">
                        <p className="text-sm font-bold text-brand-charcoal" data-id="element-927">
                          {activity.action}
                        </p>
                        <span className="text-[10px] text-brand-stone uppercase tracking-wider whitespace-nowrap ml-4" data-id="element-928">
                          {activity.time}
                        </span>
                      </div>
                      <p className="text-sm text-brand-stone" data-id="element-929">
                        {activity.detail}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <button className="w-full mt-6 py-3 text-sm font-bold text-brand-gold uppercase tracking-wider hover:bg-brand-cream rounded-lg transition-colors" data-id="element-930">
              View Full Log
            </button>
          </div>
        </ScrollReveal>
      </div>
    </div>;
}